import { useEffect, useState, type ReactNode } from 'react';
import { CONDITION_OPS, CONDITION_OPS_NO_VALUE, COUNTRIES, flagEmoji, normaliseFormsBaseUrl } from '@jfh/intake-shared';
import type { ConditionOp, IqDerivation } from '@jfh/intake-shared';
import { api } from '../../lib/api.js';
import { useToast } from '../../components/Toast.js';
import { Icon } from '../../components/Icon.js';
import { derivationSentences } from './logic.js';

/** A settings panel: a titled card, or just its body inside a Connections row. */
export function Panel({ title, bare, children }: { title: string; bare?: boolean; children: ReactNode }) {
  if (bare) return <div>{children}</div>;
  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <strong style={{ fontSize: 14.5 }}>{title}</strong>
      {children}
    </div>
  );
}

export function CopyRow({ text }: { text: string }) {
  const toast = useToast();
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
      <code
        className="mono"
        style={{
          flex: 1,
          fontSize: 12,
          padding: '8px 10px',
          background: 'var(--bg-sunken)',
          borderRadius: 'var(--r-md)',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </code>
      <button
        className="btn sm"
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          toast({ title: 'Copied', icon: 'check' });
        }}
      >
        <Icon name="copy" size={13} /> Copy
      </button>
    </div>
  );
}

/** Global country dropdown config: default + which countries are enabled. */
export function CountriesPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [disabled, setDisabled] = useState<Set<string>>(new Set());
  const [def, setDef] = useState('GB');
  const [q, setQ] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getCountryConfig()
      .then((c) => {
        setDisabled(new Set((c.disabled ?? []).map((s) => s.toUpperCase())));
        setDef((c.default || 'GB').toUpperCase());
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const toggle = (iso: string) =>
    setDisabled((prev) => {
      const n = new Set(prev);
      if (n.has(iso)) n.delete(iso);
      else n.add(iso);
      return n;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.putCountryConfig({ disabled: [...disabled], default: def });
      toast({ title: 'Countries saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const needle = q.trim().toLowerCase();
  const list = COUNTRIES.filter(
    (c) => !needle || `${c.name} ${c.dial} ${c.iso2}`.toLowerCase().includes(needle),
  );
  const enabledCount = COUNTRIES.length - disabled.size;

  return (
    <Panel title="Countries" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Controls the phone country-code and address country dropdowns on public
        forms. Untick a country to hide it everywhere. {enabledCount} of{' '}
        {COUNTRIES.length} enabled.
      </p>
      {!loaded ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : (
        <>
          <label className="field-label" style={{ marginTop: 12 }}>Default country</label>
          <select className="select" value={def} onChange={(e) => setDef(e.target.value)}>
            {COUNTRIES.filter((c) => !disabled.has(c.iso2)).map((c) => (
              <option key={c.iso2} value={c.iso2}>
                {flagEmoji(c.iso2)} {c.name} ({c.dial})
              </option>
            ))}
          </select>

          <label className="field-label" style={{ marginTop: 12 }}>Enabled countries</label>
          <input
            className="input"
            placeholder="Search countries…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div
            style={{
              marginTop: 8,
              maxHeight: 260,
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 8,
            }}
          >
            {list.map((c) => (
              <label
                key={c.iso2}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '7px 12px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!disabled.has(c.iso2)}
                  onChange={() => toggle(c.iso2)}
                />
                <span>{flagEmoji(c.iso2)}</span>
                <span style={{ flex: 1 }}>{c.name}</span>
                <span className="mono" style={{ color: 'var(--text-3)' }}>{c.dial}</span>
              </label>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save countries'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

/** Resend email connection (API key + From address) and a test send. */
export function EmailConnectionPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [from, setFrom] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [keySet, setKeySet] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api
      .getEmailConnection()
      .then((c) => {
        setFrom(c.from ?? '');
        setKeySet(Boolean(c.apiKeySet));
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.putEmailConnection({ from, resendApiKey: apiKey || undefined });
      if (apiKey) setKeySet(true);
      setApiKey('');
      toast({ title: 'Email connection saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!testTo.trim()) return;
    setTesting(true);
    try {
      const r = await api.testEmail(testTo.trim());
      if (!r.configured) toast({ title: 'No API key set', body: 'Save a Resend key first.', icon: 'shield' });
      else if (r.status === 'sent') toast({ title: 'Test email sent', body: `to ${testTo.trim()}`, icon: 'check' });
      else toast({ title: 'Test failed', body: r.message ?? r.status, icon: 'shield' });
    } catch (e) {
      toast({ title: 'Test failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Panel title="Email (Resend)" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Sends applicant emails and iQ failure alerts via Resend. The API key is
        stored server-side and never returned to the browser. The From address
        must be on a domain verified in your Resend account.
      </p>
      {!loaded ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : (
        <>
          <label className="field-label" style={{ marginTop: 12 }}>From address</label>
          <input
            className="input"
            placeholder="accounts@jfhhorticultural.com"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />

          <label className="field-label" style={{ marginTop: 12 }}>Resend API key</label>
          <input
            className="input"
            type="password"
            placeholder={keySet ? '•••••• (set)' : 're_…'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <div className="field-help" style={{ marginTop: 4 }}>
            Leave blank to keep the stored key.
          </div>

          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save connection'}
            </button>
          </div>

          <div className="divider" style={{ margin: '16px 0' }} />
          <label className="field-label">Send a test email</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button className="btn" onClick={test} disabled={testing || !testTo.trim()}>
              {testing ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

/** Recipients emailed when an iQ submission fails. */
export function IqAlertEmailsPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getIqAlertEmails()
      .then((r) => setText(r.emails.join(', ')))
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const emails = text
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.putSetting('iq_alert_emails', emails);
      setText(emails.join(', '));
      toast({ title: 'iQ alert recipients saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="iQ failure alerts" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Email these recipients whenever a submission fails to reach iQ (with the
        form, applicant, and the reason iQ returned). Comma- or line-separated.
        Leave blank for no alerts.
      </p>
      {!loaded ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : (
        <>
          <label className="field-label" style={{ marginTop: 12 }}>Recipients</label>
          <textarea
            className="textarea"
            rows={2}
            placeholder="accounts@jfhhorticultural.com, ops@jfhhorticultural.com"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save recipients'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * Org-wide iQ derivations: compute one iQ field from another via a value table
 * (e.g. posting map from currency), applied to every form's iQ payload.
 */
export function IqDerivationsPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [rows, setRows] = useState<IqDerivation[] | null>(null);
  const [saving, setSaving] = useState(false);
  // Read as sentences first; the value-table editor is behind "Change these".
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api
      .getIqDerivations()
      .then((r) => setRows(r.derivations))
      .catch(() => setRows([]));
  }, []);

  const update = (i: number, patch: Partial<IqDerivation>) =>
    setRows((rs) => (rs ?? []).map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((rs) => [
      ...(rs ?? []),
      { id: crypto.randomUUID().slice(0, 8), name: '', sourcePath: '', targetPath: '', rules: [], fallback: '' },
    ]);
  const removeRow = (i: number) => setRows((rs) => (rs ?? []).filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.putSetting('iq_derivations', rows ?? []);
      toast({ title: 'iQ derivations saved', icon: 'check' });
      setEditing(false);
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Values worked out for every form" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Fill one iQ field from another on every form&rsquo;s record, e.g. the posting map from the currency. They only
        fill a field the form hasn&rsquo;t already set.
      </p>

      {rows === null ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : !editing ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {rows.length === 0 && <div className="field-help">None yet.</div>}
          {rows.map((d) => (
            <div key={d.id} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--bg-surface)' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{d.name || `${d.targetPath} from ${d.sourcePath}`}</div>
              {derivationSentences(d).map((t, k) => (
                <div key={k} style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 3 }}>
                  {t}
                </div>
              ))}
            </div>
          ))}
          <div>
            <button className="btn sm" onClick={() => setEditing(true)}>
              <Icon name="pencil" size={13} /> Change these
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
          {rows.map((d, i) => (
            <div
              key={d.id}
              style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="input"
                  style={{ flex: 1, height: 32, fontSize: 12.5 }}
                  placeholder="Name (e.g. Posting map from currency)"
                  value={d.name ?? ''}
                  onChange={(e) => update(i, { name: e.target.value })}
                />
                <button
                  className="btn"
                  aria-label="Remove derivation"
                  style={{ height: 32, width: 32, padding: 0, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() => removeRow(i)}
                >
                  <Icon name="trash" size={14} style={{ color: 'var(--text-3)' }} />
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="input mono"
                  style={{ flex: 1, height: 32, fontSize: 12.5, minWidth: 0 }}
                  placeholder="source path · DefaultCurrency.Code"
                  value={d.sourcePath}
                  onChange={(e) => update(i, { sourcePath: e.target.value })}
                />
                <Icon name="chevronR" size={14} style={{ color: 'var(--text-3)', flex: 'none' }} />
                <input
                  className="input mono"
                  style={{ flex: 1, height: 32, fontSize: 12.5, minWidth: 0 }}
                  placeholder="target path · PostingMap.Code"
                  value={d.targetPath}
                  onChange={(e) => update(i, { targetPath: e.target.value })}
                />
              </div>
              {d.rules.map((r, ri) => {
                const op = r.op ?? 'eq';
                const noValue = CONDITION_OPS_NO_VALUE.includes(op);
                return (
                  <div key={ri} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 'none' }}>when source</span>
                    <select
                      className="select"
                      style={{ width: 120, height: 30, fontSize: 12, flex: 'none' }}
                      value={op}
                      onChange={(e) =>
                        update(i, {
                          rules: d.rules.map((x, k) =>
                            k === ri ? { ...x, op: e.target.value as ConditionOp } : x,
                          ),
                        })
                      }
                    >
                      {CONDITION_OPS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {!noValue && (
                      <input
                        className="input"
                        style={{ flex: 1, height: 30, fontSize: 12, minWidth: 0 }}
                        placeholder="value"
                        value={r.equals}
                        onChange={(e) =>
                          update(i, { rules: d.rules.map((x, k) => (k === ri ? { ...x, equals: e.target.value } : x)) })
                        }
                      />
                    )}
                    <Icon name="chevronR" size={13} style={{ color: 'var(--text-3)', flex: 'none' }} />
                    <input
                      className="input"
                      style={{ flex: 1, height: 30, fontSize: 12, minWidth: 0 }}
                      placeholder="set target ="
                      value={r.value}
                      onChange={(e) =>
                        update(i, { rules: d.rules.map((x, k) => (k === ri ? { ...x, value: e.target.value } : x)) })
                      }
                    />
                    <button
                      className="btn"
                      aria-label="Remove rule"
                      style={{ height: 30, width: 30, padding: 0, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => update(i, { rules: d.rules.filter((_, k) => k !== ri) })}
                    >
                      <Icon name="x" size={12} style={{ color: 'var(--text-3)' }} />
                    </button>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => update(i, { rules: [...d.rules, { equals: '', value: '' }] })}
                >
                  <Icon name="plus" size={13} style={{ color: 'var(--accent)' }} /> Add rule
                </button>
                <input
                  className="input"
                  style={{ flex: 1, height: 32, fontSize: 12.5, minWidth: 140 }}
                  placeholder="Fallback when no rule matches (optional)"
                  value={d.fallback ?? ''}
                  onChange={(e) => update(i, { fallback: e.target.value })}
                />
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={addRow}>
              <Icon name="plus" size={14} style={{ color: 'var(--accent)' }} /> Add derivation
            </button>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save derivations'}
            </button>
            <button
              className="btn"
              onClick={() => {
                // Throw away unsaved edits: show what's saved.
                api
                  .getIqDerivations()
                  .then((r) => setRows(r.derivations))
                  .catch(() => undefined);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function FormsUrlPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getFormsBaseUrl()
      .then((r) => setUrl(r.url))
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = normaliseFormsBaseUrl(url);
      await api.putSetting('forms_base_url', clean);
      setUrl(clean);
      toast({ title: 'Public forms URL saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Public forms URL" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Where the public form renderer is served. Used by the embed snippet and the
        "Test form" link. Set this to your custom domain once it's live (e.g.{' '}
        <span className="mono">https://forms.jfhhorticultural.com</span>); until then
        leave it as the Pages URL.
      </p>
      {!loaded ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : (
        <>
          <label className="field-label" style={{ marginTop: 12 }}>Base URL</label>
          <input
            className="input"
            placeholder="https://jfh-forms.pages.dev"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save forms URL'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

export function WixWebhookPanel({ bare }: { bare?: boolean } = {}) {
  const toast = useToast();
  const [secret, setSecret] = useState('');
  const [url, setUrl] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getWixWebhook()
      .then((r) => {
        setSecret(r.secret ?? '');
        setUrl(r.url ?? '');
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const gen = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(40);
    crypto.getRandomValues(bytes);
    setSecret(Array.from(bytes, (b) => chars[b % chars.length]).join(''));
  };

  const save = async () => {
    if (!secret.trim()) {
      toast({ title: 'Enter or generate a secret first', icon: 'shield' });
      return;
    }
    setSaving(true);
    try {
      await api.putWixWebhook(secret.trim());
      const r = await api.getWixWebhook();
      setSecret(r.secret ?? '');
      setUrl(r.url ?? '');
      toast({ title: 'Wix webhook saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast({ title: 'Copied', icon: 'check' }),
      () => toast({ title: 'Copy failed', icon: 'shield' }),
    );
  };

  return (
    <Panel title="Wix webhook" bare={bare}>
      <p className="field-help" style={{ marginTop: 6 }}>
        Paste this URL into your Wix automation's “Send via webhook” action. The secret
        authenticates the request (Wix can't sign it). Each Wix form is then linked to a portal
        form by its <span className="mono">formId</span> in the form builder.
      </p>
      {!loaded ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : (
        <>
          <label className="field-label" style={{ marginTop: 12 }}>Secret</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input mono"
              value={secret}
              placeholder="(none set)"
              onChange={(e) => setSecret(e.target.value.trim())}
            />
            <button className="btn sm" style={{ flex: 'none' }} onClick={gen}>
              Generate
            </button>
          </div>

          <label className="field-label" style={{ marginTop: 12 }}>Webhook URL</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input mono"
              readOnly
              value={url}
              placeholder="Save a secret to see the URL"
            />
            <button
              className="btn sm"
              style={{ flex: 'none' }}
              disabled={!url}
              onClick={() => copy(url)}
            >
              <Icon name="copy" size={13} /> Copy
            </button>
          </div>
          <div className="field-help" style={{ marginTop: 4 }}>
            Changing the secret invalidates the old URL — update it in Wix after saving.
          </div>

          <div style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}
